//
// Naming inline scripts.
//
// When an inline `<script>` throws, the browser attributes the frame to the
// *document* URL rather than to a script of its own - `https://scite.ai/`, not a
// filename. That is indistinguishable from the minified bridge an in-app WebView
// injects into the page, which is the whole difficulty behind SCI-3891: the noise
// filter in `error-noise.ts` can only read page attribution as "this stack is not
// ours" while none of our own inline scripts look that way too.
//
// A `//# sourceURL=` directive fixes that at the source. The engine adopts the
// name for the script, so frames carry it instead of the document URL. Verified
// in Chromium and Firefox: an unnamed inline script reports `/somepage`, the same
// script with a directive reports the name given here.
//
// WebKit is the exception, and it matters. It honours the directive for `eval`
// but ignores it for inline `<script>` tags - checked against WebKit 26.4 with
// the directive leading, trailing, absolute, relative and in the `//@` spelling.
// So on iOS these scripts are still attributed to the page, and `error-noise.ts`
// keeps its narrow blind spot for the WebKit in-app browsers. Everywhere else -
// desktop, and the Chromium-backed Android in-app browsers - naming holds.
//
// Names are `.js` paths so `isPageAttributedFrame` sees a script rather than a
// page. They stay relative - the engine reports them verbatim, so one name works
// across scite.ai, staging and localhost.
//
const SCRIPT_TAG = /<script\b([^>]*)>([\s\S]*?)<\/script>/gi
// `\b` would also match the `src` in `data-src`, so require a real attribute break.
const HAS_SRC = /(?:^|\s)src\s*=/i
const DIRECTIVE = '//# sourceURL='
//
// Already named means a directive line of its own at the end, not the characters
// appearing anywhere in the script. Code carries data, and data can quote
// anything - a search query, a config value, a paper title.
//
const ALREADY_NAMED = /(?:^|\n)\/\/# sourceURL=\S*\s*$/

/** Name a block of inline JavaScript, so its stack frames carry that name. */
export const nameInlineScript = (code: string, name: string): string =>
  ALREADY_NAMED.test(code) ? code : `${code}\n${DIRECTIVE}/inline/${name}.js`

// Likewise `\b` would match the `type` in `data-type`.
const TYPE_ATTR = /(?:^|\s)type\s*=\s*["']?([^"'\s>]+)/i
const JS_TYPE = /^(?:module|(?:text|application)\/(?:java|ecma)script)$/i

//
// A script with a `src` already has a URL of its own, so its frames were never
// page-attributed; an empty one has no frames to attribute at all. Neither needs
// a name, and skipping them keeps the numbering below tied to real scripts.
//
// The page also carries `<script>` blocks that are data rather than code -
// JSON-LD, loadable's chunk manifests, import maps. They never produce a frame,
// and a `//` line appended to them is not a comment, it is malformed JSON.
//
const isNameable = (attrs: string, body: string): boolean => {
  if (HAS_SRC.test(attrs) || body.trim() === '') {
    return false
  }
  const type = attrs.match(TYPE_ATTR)
  // No type at all means classic JavaScript.
  return !type || JS_TYPE.test(type[1])
}

/**
 * Name every inline script in a block of HTML. Blocks holding more than one get
 * a numeric suffix, so two faults in the same vendor snippet stay distinct.
 */
export const nameInlineScripts = (html: string, name: string): string => {
  let total = 0
  html.replace(SCRIPT_TAG, (match, attrs: string, body: string) => {
    if (isNameable(attrs, body)) {
      total++
    }
    return match
  })

  if (total === 0) {
    return html
  }

  let index = 0
  return html.replace(SCRIPT_TAG, (match, attrs: string, body: string) => {
    if (!isNameable(attrs, body)) {
      return match
    }
    index++
    return `<script${attrs}>${nameInlineScript(body, total > 1 ? `${name}-${index}` : name)}</script>`
  })
}
